/**
 * useOutreachNegotiation — one entry's composer actions: helpMeWrite and the acknowledgement/confirmation/counter drafts
 * (never writes a fact, so it never invalidates anything — the composer just
 * fills its textarea from the result), send (a human outbound: reply/confirm/
 * counter) and agreeRate (the manual rate-lock path, ruling 54).
 *
 * Each mutation mints its own Idempotency-Key per call (helpMeWrite needs none —
 * a draft is not a fact) — the same per-call-key convention useOutreachThreadActions
 * uses, since a send/agree here is a single click, not a retryable
 * multi-step session like the wizard's useCreateFirstTouchDrafts.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { OutreachHumanSendInput, OutreachRate, OutreachRow, OutreachSuggestion, OutreachTemplateStepText } from '@discovr/types';
import { campaignKeys } from '../queryKeys';
import type { OutreachHumanSendResult } from '../api/outreachApi';
import type { HelpMeWriteInput } from '../api/outreachApi';
import {
  agreeOutreachRate,
  fetchAcknowledgementDraft,
  fetchConfirmationDraft,
  fetchCounterDraft,
  fetchFollowupDraft,
  fetchNudgeDraft,
  fetchReplySuggestion,
  fetchTemplateStepText,
  helpMeWriteOutreachReply,
  type HelpMeWriteStreamOptions,
  sendOutreachMessage,
} from '../api/outreachApi';

type RowsPage = { rows: OutreachRow[]; total: number };

const ANSWERING_STATUSES = new Set(['queued', 'sending', 'sent']);

const answered = (r: OutreachRow): OutreachRow => ({ ...r, awaitingReply: false, needsYou: !!r.escalatedAt });

/** Both cache shapes under the rows prefix: a table page and the single-row read (null when missing). */
function withAnswered(data: unknown, entryId: string): unknown {
  if (!data || typeof data !== 'object') return data;
  if ('rows' in data && Array.isArray((data as RowsPage).rows)) {
    const page = data as RowsPage;
    return { ...page, rows: page.rows.map((r) => (r.entryId === entryId ? answered(r) : r)) };
  }
  const row = data as OutreachRow;
  return row.entryId === entryId ? answered(row) : data;
}

export function useOutreachNegotiation(campaignId: string, entryId: string) {
  const qc = useQueryClient();

  const invalidate = (rows = true) => {
    if (rows) {
      void qc.invalidateQueries({ queryKey: campaignKeys.outreachRowsAll(campaignId) });
      void qc.invalidateQueries({ queryKey: campaignKeys.outreachRow(campaignId, entryId) });
    }
    void qc.invalidateQueries({ queryKey: campaignKeys.outreachCounts(campaignId) });
    void qc.invalidateQueries({ queryKey: campaignKeys.outreachThread(entryId) });
  };

  // A queued send from the reply box clears its saved draft server-side; any other send leaves it.
  // A send that answers the creator (queued, sending, sent) clears the flag locally and leaves the row
  // queries alone: the server drops it in a background refold, so a refetch now would bring it back.
  // outreach_entry_projected refreshes the rows once the refold lands.
  const afterSend = (result: OutreachHumanSendResult, input: OutreachHumanSendInput) => {
    const answers = !input.followup && ANSWERING_STATUSES.has(result.status);
    if (answers) qc.setQueriesData({ queryKey: campaignKeys.outreachRowsAll(campaignId) }, (data: unknown) => withAnswered(data, entryId));
    invalidate(!answers);
    if (input.clearComposerDraft) qc.setQueryData(campaignKeys.outreachComposerDraft(entryId), null);
  };

  // Never invalidates: a draft lives in the composer's editor, not a stored
  // fact — nothing downstream changed yet.
  const helpMeWrite = useMutation<OutreachSuggestion, Error, HelpMeWriteInput & HelpMeWriteStreamOptions>({
    mutationFn: ({ signal, onToken, ...input }) => helpMeWriteOutreachReply(campaignId, entryId, input, { signal, onToken }),
  });

  const send = useMutation<OutreachHumanSendResult, Error, OutreachHumanSendInput>({
    mutationFn: (input) => sendOutreachMessage(entryId, input, crypto.randomUUID()),
    onSuccess: afterSend,
  });

  const agreeRate = useMutation<{ round: number; humanRound?: number; totalRound?: number }, Error, OutreachRate[]>({
    mutationFn: (rates) => agreeOutreachRate(entryId, rates, crypto.randomUUID()),
    onSuccess: () => invalidate(),
  });

  // Never invalidate, like helpMeWrite: a draft is text for the composer, not a fact.
  const acknowledgementDraft = useMutation<OutreachSuggestion, Error, void>({
    mutationFn: () => fetchAcknowledgementDraft(entryId),
  });

  const confirmationDraft = useMutation<OutreachSuggestion, Error, OutreachRate[]>({
    mutationFn: (rates) => fetchConfirmationDraft(entryId, rates),
  });

  // Never invalidate, like confirmationDraft: a draft is text for the composer, not a fact.
  const counterDraft = useMutation<OutreachSuggestion, Error, OutreachRate[]>({
    mutationFn: (rates) => fetchCounterDraft(entryId, rates),
  });

  // The Follow up pill (P35). NO_FOLLOWUP_DUE means this row is stale (the creator replied, or it was paused), so refresh it.
  const followupDraft = useMutation<OutreachSuggestion, Error, void>({
    mutationFn: () => fetchFollowupDraft(entryId),
    onError: (e) => {
      if ((e as { code?: string }).code !== 'NO_FOLLOWUP_DUE') return;
      void qc.invalidateQueries({ queryKey: campaignKeys.outreachRowsAll(campaignId) });
      void qc.invalidateQueries({ queryKey: campaignKeys.outreachRow(campaignId, entryId) });
    },
  });

  const nudgeDraft = useMutation<OutreachSuggestion, Error, void>({
    mutationFn: () => fetchNudgeDraft(entryId),
  });

  const replySuggestion = useMutation<OutreachSuggestion, Error, void>({
    mutationFn: () => fetchReplySuggestion(entryId),
  });

  const templateStep = useMutation<OutreachTemplateStepText, Error, number>({
    mutationFn: (step) => fetchTemplateStepText(entryId, step),
  });

  return { helpMeWrite, send, agreeRate, acknowledgementDraft, confirmationDraft, counterDraft, followupDraft, nudgeDraft, replySuggestion, templateStep };
}
